import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Check, ArrowRight } from 'lucide-react';

const Pricing = () => {
  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const benefits = [
    'No upfront costs or setup fees',
    'Only pay based on collections',
    'Industry-standard rate: 3-8% of monthly collections',
    'Includes all services: coding, billing, claims, AR',
    'Real-time dashboard and reporting',
    'Dedicated account manager',
    'Cancel anytime with 30-day notice',
  ];

  return (
    <section id="pricing" className="py-32 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="text-[#14b8a6] font-semibold text-sm tracking-wider uppercase mb-4">
            Transparent Pricing
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Performance-Based Pricing
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            We only succeed when you succeed. Our percentage-based model aligns
            our incentives with your revenue goals.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <Card className="border-2 border-[#14b8a6]/30 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-br from-[#1e3a8a] to-[#0f2557] text-white p-12 text-center">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
                className="inline-block mb-4"
              >
                <div className="text-6xl lg:text-7xl font-bold">3-8%</div>
              </motion.div>
              <div className="text-xl text-slate-200 mb-2">
                of monthly collections
              </div>
              <div className="text-slate-300">
                Custom rate based on practice size & specialty
              </div>
            </div>

            <CardContent className="p-12">
              <div className="space-y-4 mb-10">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#14b8a6]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check size={16} className="text-[#14b8a6]" />
                    </div>
                    <span className="text-slate-700 text-lg">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="text-center"
              >
                <Button
                  onClick={scrollToContact}
                  size="lg"
                  className="bg-gradient-to-r from-[#1e3a8a] to-[#14b8a6] hover:from-[#1e3a8a]/90 hover:to-[#14b8a6]/90 text-white px-10 py-6 text-lg rounded-full group transition-all duration-300 hover:shadow-2xl hover:shadow-[#1e3a8a]/30"
                >
                  Request Custom Quote
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
